import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import Paper from '@material-ui/core/Paper'
import Checkbox from '@material-ui/core/Checkbox'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import _ from 'lodash'
import clsx from 'clsx'

const desc = (a, b, orderBy) => {
  if (_.get(b, orderBy) < _.get(a, orderBy)) {
    return -1
  }
  if (_.get(b, orderBy) > _.get(a, orderBy)) {
    return 1
  }
  return 0
}

const stableSort = (array, cmp, page, rowsState, setRowsState, setPage) => {
  if (array.length !== rowsState.length) {
    page !== 0 && setPage(0)
    setRowsState(array)
  }
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0])
    if (order !== 0) return order

    return a[1] - b[1]
  })

  return stabilizedThis.map(el => el[0])
}

const getSorting = (order, orderBy) => order === 'desc'
  ? (a, b) => desc(a, b, orderBy)
  : (a, b) => -desc(a, b, orderBy)

const EnhancedTableHead = props => {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    withSelect,
    headCells,
  } = props

  const createSortHandler = property => event => onRequestSort(event, property)

  return (
    <TableHead>
      <TableRow>
        {withSelect && (
          <TableCell padding='checkbox'>
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'select all desserts' }}
            />
          </TableCell>
        )}
        {headCells.map(headCell => (
          <TableCell
            key={headCell.key}
            sortDirection={orderBy === headCell.key ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.key}
              direction={order}
              onClick={createSortHandler(headCell.key)}
            >
              {headCell.label}
              {orderBy === headCell.key ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  paper: {
    width: '100%',
    margin: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    flexWrap: 'wrap',
  },
  titleToolbar: {
    flex: '1 1 100%',
  },
}))

const TableComponent = props => {
  const {
    withSelect,
    selected = [],
    selectedAttr,
    onSelect,
    onClickRow,
    headCells,
    rows,
    title,
    defaultOrder,
    defaultOrderBy,
    style,
    className,
    renderIconsHeader,
    minRowsLength,
  } = props
  const classes = useStyles()
  const [rowsState, setRowsState] = React.useState([rows, 0])
  const [order, setOrder] = React.useState(defaultOrder || 'desc')
  const [orderBy, setOrderBy] = React.useState(defaultOrderBy || '')
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(minRowsLength || 5)
  const theadRef = React.useRef(null)
  const toolBarRef = React.useRef(null)
  const [toolbarHeight, setToolbarHeight] = React.useState(0)

  React.useEffect(() => {
    setToolbarHeight(toolBarRef.current
      ? toolBarRef.current.offsetHeight
      : 0)
  }, [toolBarRef, rows])

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === 'desc'
    setOrder(isDesc ? 'asc' : 'desc')
    setOrderBy(property)
  }

  const handleSelectAllClick = attr => event => {
    if (event.target.checked) {
      const newSelecteds = rows.map(n => n[attr])
      onSelect(newSelecteds)

      return
    }
    onSelect([])
  }

  const handleSelect = (event, value) => {
    const selectedIndex = selected.indexOf(value)
    let newSelected = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, value)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }

    onSelect(newSelected)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const isSelected = value => selected.indexOf(value) !== -1

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage)

  return (
    <Paper className={clsx(classes.paper, className)} style={style}>
      <Toolbar className={classes.toolbar} ref={toolBarRef}>
        <Grid container>
          <Grid item xs={12} md={6}>
            <Typography
              className={classes.titleToolbar}
              variant='h6'
              id='tableTitle'
            >
              {title}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            {renderIconsHeader && renderIconsHeader({ rows })}
          </Grid>
        </Grid>
      </Toolbar>
      <div
        className={classes.tableWrapper}
        style={{ height: `calc(100% - (56px + ${toolbarHeight}px))` }}
      >
        <Table
          className={classes.table}
          aria-labelledby='tableTitle'
          size='medium'
          aria-label='enhanced table'
        >
          <EnhancedTableHead
            ref={theadRef}
            classes={classes}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick(selectedAttr)}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            withSelect={withSelect}
            headCells={headCells}
          />
          <TableBody>
            {stableSort(rows, getSorting(order, orderBy), page, rowsState, setRowsState, setPage)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const isItemSelected = isSelected(row[selectedAttr])
                const labelId = `enhanced-table-checkbox-${index}`

                return (
                  <TableRow
                    hover
                    onClick={event => {
                      withSelect && handleSelect(event, row[selectedAttr])
                      onClickRow && onClickRow(row)
                    }}
                    role='checkbox'
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={index.toString()}
                    selected={isItemSelected}
                    style={{ cursor: 'pointer' }}
                  >
                    {withSelect && (
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                    )}
                    {headCells.map((header, indexTwo) => (
                      <TableCell
                        key={`${index.toString()}-${indexTwo.toString()}`}
                      >
                        {_.get(row, header.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 63 * emptyRows }}>
                <TableCell
                  colSpan={withSelect ? headCells.length + 1 : headCells.length}
                  align='center'
                >
                  {rows.length === 0 ? 'No hay datos para mostrar' : ''}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={{
          'aria-label': 'previous page',
        }}
        nextIconButtonProps={{
          'aria-label': 'next page',
        }}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Paper>
  )
}

TableComponent.propTypes = {
  withSelect: PropTypes.bool,
  selected: PropTypes.array,
  onSelect: PropTypes.func,
  onClickRow: PropTypes.func,
  headCells: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  rows: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  defaultOrder: PropTypes.string,
  defaultOrderBy: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  renderIconsHeader: PropTypes.func,
  selectedAttr: PropTypes.string,
  minRowsLength: PropTypes.number,
}

export default TableComponent
